import { useCallback, useContext, useState } from "react";

import { AuthAppContext } from "components/root/auth-app-provider";
import { useKeystone } from "data/ss-data-hooks";
import { UnauthorizedRequestError, isError } from "data/ss-error";
import { type TingContextState } from "pages/offer/ting/ting-flow";
import { handleUnauthorized } from "utils";

export type TingEnrollmentValues = {
  offeringId?: string;
  offerId?: string;
  insightPolicyId?: string;
  additionalDetails?: {
    firstName?: string;
    lastName?: string;
    emailAddress?: string;
    phone?: string;
    isMobilePhone?: boolean;
    consentSms?: boolean;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
  };
};

export const useTingEnrollment = (state: TingContextState) => {
  const keystone = useKeystone();
  const { policy } = useContext(AuthAppContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const {
    firstName,
    lastName,
    emailAddress,
    phone,
    mobile,
    offeringId,
    offerId,
  } = state ?? {};

  const enrollTing = useCallback(async () => {
    setLoading(true);
    setError(false);
    const tingEnrollmentBody: TingEnrollmentValues = {
      offeringId,
      offerId,
      insightPolicyId: policy.insightPolicyId,
      additionalDetails: {
        firstName,
        lastName,
        emailAddress,
        phone,
        isMobilePhone: mobile?.includes("isMobile"),
        consentSms: mobile?.includes("isTextOk"),
        addressLine1: policy?.propertyAddress?.street1,
        addressLine2: policy.propertyAddress?.street2,
        city: policy.propertyAddress?.city,
        state: policy.propertyAddress?.state,
        postalCode: policy.propertyAddress?.zip,
      },
    };

    const response = await keystone.enrollTing(tingEnrollmentBody);
    if (isError(response)) {
      if (response instanceof UnauthorizedRequestError) {
        handleUnauthorized({ endpoint: "homeservices/order/ting" });
      } else {
        setError(true);
      }
    }
    setLoading(false);

    return !isError(response);
  }, [
    emailAddress,
    firstName,
    keystone,
    lastName,
    mobile,
    offerId,
    offeringId,
    phone,
    policy.insightPolicyId,
    policy.propertyAddress?.city,
    policy.propertyAddress?.state,
    policy.propertyAddress?.street1,
    policy.propertyAddress?.street2,
    policy.propertyAddress?.zip,
  ]);

  return { enrollTing, loading, error };
};
